/**
 * The personal pass QR, parsed in the browser (registration v2, S7).
 *
 * Pure: no imports, no DOM. The QR Reader decodes whatever the camera saw and
 * hands the text here; the token that comes back is what the arm callable
 * receives. The shape and the URL handling mirror the server exactly
 * (functions/src/services/qrToken.ts: QR_TOKEN_RE and normalizeQrToken), so
 * the browser accepts precisely what armParticipant accepts and nothing
 * looser.
 */

// The personal token: 32 random bytes as base64url, so 43 characters from
// [A-Za-z0-9_-] and no padding. A 32-hex job token can never pass.
export const QR_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/**
 * The personal token from whatever the scanner read: the bare token, or the
 * pass URL (https://nycsuite.io/p/{token}, any origin) with or without a
 * query, a fragment or surrounding whitespace. Takes the segment after the
 * LAST "/p/" and cuts it at the first "?", "#" or whitespace, then requires
 * the token shape. null means "not a personal token": a job token, an
 * unrelated URL, garbage.
 */
export function parsePersonalQr(raw) {
  let s = String(raw ?? "").trim();
  const at = s.lastIndexOf("/p/");
  if (at >= 0) {
    s = s.slice(at + 3);
    const cut = s.search(/[?#\s]/);
    if (cut >= 0) s = s.slice(0, cut);
  }
  return QR_TOKEN_RE.test(s) ? s : null;
}

/**
 * SHA-256 hex of the token, the same hex the server stores as qrTokenHash on
 * the roster entry, so the reader can find the entry in the live roster
 * before the arm. "" when WebCrypto is unavailable (plain http on the studio
 * LAN): the confirm card then reads "not in the list" and the server still
 * resolves the token on Send.
 */
export async function hashQrToken(token) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof subtle.digest !== "function" || typeof TextEncoder === "undefined") return "";
  try {
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(String(token ?? "")));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
}
